import { type ReactNode, useState } from "react";
import { AlertTriangle, CheckCircle2, HelpCircle, Info } from "lucide-react";
import CleButton, { type CleButtonVariant } from "./CleButton";
import CleModal, { CleModalRoot, CleModalTrigger } from "./CleModal";

type ConfirmationModalConfirmHelpers = {
  close: () => void;
};

type ConfirmationModalProps = {
  cancelButtonId?: string;
  cancelLabel?: string;
  children?: ReactNode;
  confirmButtonId?: string;
  confirmLabel?: string;
  confirmVariant?: CleButtonVariant;
  description: string;
  isConfirmDisabled?: boolean;
  isConfirming?: boolean;
  onConfirm: (helpers: ConfirmationModalConfirmHelpers) => void;
  title: ReactNode;
  trigger: ReactNode;
};

const getVariantPresentation = (variant: CleButtonVariant) => {
  if (variant === "destructive") {
    return {
      className: "cle-confirmation-modal__icon--destructive",
      icon: <AlertTriangle size={26} aria-hidden strokeWidth={1.8} />,
    };
  }

  if (variant === "secondary" || variant === "outline" || variant === "ghost") {
    return {
      className: "cle-confirmation-modal__icon--secondary",
      icon: <HelpCircle size={26} aria-hidden strokeWidth={1.8} />,
    };
  }

  if (variant === "link") {
    return {
      className: "cle-confirmation-modal__icon--info",
      icon: <Info size={26} aria-hidden strokeWidth={1.8} />,
    };
  }

  return {
    className: "cle-confirmation-modal__icon--default",
    icon: <CheckCircle2 size={26} aria-hidden strokeWidth={1.8} />,
  };
};

const ConfirmationModal = ({
  cancelButtonId,
  cancelLabel = "Afbryd",
  children,
  confirmButtonId,
  confirmLabel = "Bekræft",
  confirmVariant = "default",
  description,
  isConfirmDisabled = false,
  isConfirming = false,
  onConfirm,
  title,
  trigger,
}: ConfirmationModalProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const variantPresentation = getVariantPresentation(confirmVariant);

  const close = () => setIsOpen(false);

  const handleOpenChange = (open: boolean) => {
    if (isConfirming) {
      return;
    }

    setIsOpen(open);
  };

  const handleConfirm = () => {
    onConfirm({ close });
  };

  return (
    <CleModalRoot
      controller={{
        open: isOpen,
        setOpen: setIsOpen,
        onOpenChange: handleOpenChange,
      }}
    >
      <CleModalTrigger asChild>{trigger}</CleModalTrigger>
      <CleModal
        className="cle-confirmation-modal"
        title={title}
        description={description}
        dialog={isConfirming}
        size="sm"
        footer={
          <div className="d-flex justify-content-center gap-3 w-100 px-3 py-2">
            <CleButton
              id={cancelButtonId}
              variant="outline"
              onClick={close}
              disabled={isConfirming}
            >
              {cancelLabel}
            </CleButton>
            <CleButton
              id={confirmButtonId}
              variant={confirmVariant}
              onClick={handleConfirm}
              disabled={isConfirming || isConfirmDisabled}
            >
              {isConfirming ? "Behandler..." : confirmLabel}
            </CleButton>
          </div>
        }
      >
        <div className="cle-confirmation-modal__body">
          <div
            className={`cle-confirmation-modal__icon ${variantPresentation.className}`}
          >
            {variantPresentation.icon}
          </div>
          <div className="cle-confirmation-modal__content">
            {children ?? <p className="mb-0">{description}</p>}
          </div>
        </div>
      </CleModal>
    </CleModalRoot>
  );
};

export default ConfirmationModal;
